import React, { useEffect, useRef, useState } from "react";
import Dropdown from "../DropDown";
import { FaEllipsisH, FaSmile, FaMeh, FaFrown, FaSearch } from "react-icons/fa";
import ConfirmModal from "../Modals/ConfirmModal";
import Pagination from "../Pagination";

const NotasList = ({ notas, deleteNotaData, onEdit, showNotification }) => {
  const [actionMenu, setActionMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNotaId, setSelectedNotaId] = useState(null);
  const actionMenuRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  // Estados para los filtros
  const [filteredNotas, setFilteredNotas] = useState(notas);
  const [searchValue, setSearchValue] = useState("");
  const [selectedPeriodo, setSelectedPeriodo] = useState("");
  const [selectedAsignatura, setSelectedAsignatura] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const notasPorPagina = 7;

  // 🔥 NUEVO: Detectar si hay notas de preescolar (con caritas)
  const esPreescolar = filteredNotas.some(nota => nota.simbolo_carita !== null && nota.simbolo_carita !== undefined);

  useEffect(() => {
    setFilteredNotas(notas);
  }, [notas]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setActionMenu(null);
      }
    };

    const handleResize = () => {
      setActionMenu(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleSearch = (e) => {
    setSearchValue(e.target.value.toLowerCase());
    filtrarNotas(
      e.target.value.toLowerCase(),
      selectedPeriodo,
      selectedAsignatura
    );
  };

  const handlePeriodoChange = (e) => {
    setSelectedPeriodo(e.target.value);
    filtrarNotas(searchValue, e.target.value, selectedAsignatura);
  };

  const handleAsignaturaChange = (e) => {
    setSelectedAsignatura(e.target.value);
    filtrarNotas(searchValue, selectedPeriodo, e.target.value);
  };

  const filtrarNotas = (searchTerm, periodo, asignatura) => {
    const filtered = notas.filter((nota) => {
      const matchesName =
        `${nota.first_name} ${nota.second_name} ${nota.first_surname} ${nota.second_surname}`
          .toLowerCase()
          .includes(searchTerm);
      const matchesPeriodo = periodo === "" || nota.periodo_nombre === periodo;
      const matchesAsignatura =
        asignatura === "" || nota.asignatura_nombre === asignatura;
      return matchesName && matchesPeriodo && matchesAsignatura;
    });
    setFilteredNotas(filtered);
    setCurrentPage(1);
  };

  const indexOfLastNota = currentPage * notasPorPagina;
  const indexOfFirstNota = indexOfLastNota - notasPorPagina;
  const currentNotas = filteredNotas.slice(indexOfFirstNota, indexOfLastNota);
  const totalPaginas = Math.ceil(filteredNotas.length / notasPorPagina);

  const toggleActionMenu = (userId, event) => {
    if (actionMenu === userId) {
      setActionMenu(null);
    } else {
      const buttonRect = event.currentTarget.getBoundingClientRect();
      const menuWidth = 150;
      const leftPosition = Math.min(
        buttonRect.left,
        window.innerWidth - menuWidth - 70
      );

      setActionMenu(userId);
      setActionMenuPosition({
        top: buttonRect.bottom + 10,
        left: leftPosition,
      });
    }
  };

  const openDeleteModal = (notaId) => {
    setSelectedNotaId(notaId);
    setIsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedNotaId(null);
    setIsModalOpen(false);
  };

  const handleDelete = async () => {
    if (selectedNotaId) {
      setIsLoading(true);
      try {
        await deleteNotaData(selectedNotaId);
        showNotification({
          type: "success",
          message: "Nota eliminada correctamente.",
        });
      } catch (error) {
        const message =
          error.response?.data?.message || "Error al eliminar la nota.";
        showNotification({ type: "error", message });
      } finally {
        setIsLoading(false);
        closeDeleteModal();
      }
    }
  };

  const renderCalificacion = (nota) => {
    if (!nota) return "-";

    if (nota.simbolo_carita) {
      switch (nota.simbolo_carita) {
        case "feliz":
          return (
            <div className="flex justify-center">
              <FaSmile className="text-green text-2xl" />
            </div>
          );
        case "contento":
          return (
            <div className="flex justify-center">
              <FaSmile className="text-main-blue text-2xl" />
            </div>
          );
        case "intermedio":
          return (
            <div className="flex justify-center">
              <FaMeh className="text-yellow-500 text-2xl" />
            </div>
          );
        case "triste":
          return (
            <div className="flex justify-center">
              <FaFrown className="text-red-500 text-2xl" />
            </div>
          );
        default:
          return (
            <div className="flex justify-center">
              {nota.calificacion || "-"}
            </div>
          );
      }
    }

    return (
      <div className="flex justify-center">{nota.calificacion || "-"}</div>
    );
  };

  return (
    <div className="bg-white p-6 rounded-2xl shadow-md mt-6">
      <h3 className="text-2xl font-bold text-gray-800 mb-6">Notas Registradas</h3>

      {/* Filtros responsivos con mismo estilo que NotasForm */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700">
            Buscar Estudiante
          </label>
          <div className="flex items-center border border-gray-300 rounded-lg px-4 py-3 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
            <FaSearch className="text-gray-400 mr-2" />
            <input
              id="search-input"
              type="text"
              placeholder="Buscar por nombre..."
              className="outline-none w-full"
              value={searchValue}
              onChange={handleSearch}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700">
            Periodo
          </label>
          <Dropdown
            value={selectedPeriodo}
            onChange={handlePeriodoChange}
            options={[
              { value: "", label: "Todos los periodos" },
              ...[...new Set(notas.map((nota) => nota.periodo_nombre))].map(
                (periodo) => ({ value: periodo, label: periodo })
              ),
            ]}
            placeholder="Todos los periodos"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700">
            Asignatura
          </label>
          <Dropdown
            value={selectedAsignatura}
            onChange={handleAsignaturaChange}
            options={[
              { value: "", label: "Todas las asignaturas" },
              ...[...new Set(notas.map((nota) => nota.asignatura_nombre))].map(
                (asignatura) => ({ value: asignatura, label: asignatura })
              ),
            ]}
            placeholder="Todas las asignaturas"
          />
        </div>
      </div>

      {filteredNotas.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500 text-lg">No hay notas registradas.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg shadow-sm border border-gray-200">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                  <th className="border border-gray-300 px-4 py-3 font-semibold text-left">
                    Estudiante
                  </th>
                  <th className="border border-gray-300 px-4 py-3 font-semibold text-center">
                    Asignatura
                  </th>
                  <th className="border border-gray-300 px-4 py-3 font-semibold text-center">
                    Periodo
                  </th>
                  <th className="border border-gray-300 px-4 py-3 font-semibold text-center">
                    Calificación
                  </th>
                  {/* 🔥 OCULTAR: Promedio Final para preescolar */}
                  {!esPreescolar && (
                    <th className="border border-gray-300 px-4 py-3 font-semibold text-center">
                      Promedio Final
                    </th>
                  )}
                  {/* 🔥 COMENTADO: Columna de acción por el momento */}
                  {/* <th className="border border-gray-300 px-4 py-3 font-semibold text-center">
                    Acción
                  </th> */}
                </tr>
              </thead>
              <tbody>
                {currentNotas.map((nota, index) => (
                  <tr
                    key={nota.id}
                    className={
                      index % 2 === 0
                        ? "bg-white hover:bg-blue-50"
                        : "bg-gray-50 hover:bg-blue-50"
                    }
                  >
                    <td className="border border-gray-300 px-4 py-2">
                      {nota.first_name} {nota.second_name} {nota.first_surname} {nota.second_surname}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      {nota.asignatura_nombre}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      {nota.periodo_nombre}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      {renderCalificacion(nota)}
                    </td>
                    {/* 🔥 OCULTAR: Promedio Final para preescolar */}
                    {!esPreescolar && (
                      <td className="border border-gray-300 px-4 py-2 text-center font-bold">
                        {nota.promedio_final !== null ? nota.promedio_final : "-"}
                      </td>
                    )}
                    {/* 🔥 COMENTADO: Columna de acción por el momento */}
                    {/* <td className="relative border border-gray-300 px-4 py-2 text-center">
                      <button
                        onClick={(e) => toggleActionMenu(nota.id, e)}
                        className="text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100 transition"
                      >
                        <FaEllipsisH />
                      </button>
                      {actionMenu === nota.id && (
                        <div
                          ref={actionMenuRef}
                          className="fixed w-40 bg-white rounded-lg shadow-lg z-50 border border-gray-200"
                          style={{
                            top: `${actionMenuPosition.top}px`,
                            left: `${actionMenuPosition.left}px`,
                          }}
                        >
                          <ul>
                            <li
                              className="px-4 py-2 text-blue-600 hover:bg-blue-50 cursor-pointer rounded-t-lg transition"
                              onClick={() => onEdit(nota)}
                            >
                              Actualizar
                            </li>
                            <li
                              className="px-4 py-2 text-red-600 hover:bg-red-50 cursor-pointer rounded-b-lg transition"
                              onClick={() => openDeleteModal(nota.id)}
                            >
                              Eliminar
                            </li>
                          </ul>
                        </div>
                      )}
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {filteredNotas.length > notasPorPagina && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPaginas}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      {/* 🔥 COMENTADO: Modal de eliminación por el momento */}
      {/* <ConfirmModal
        isOpen={isModalOpen}
        title="Confirmar Eliminación"
        message="¿Estás seguro de que deseas eliminar esta nota? Esta acción no se puede deshacer."
        onConfirm={handleDelete}
        onCancel={closeDeleteModal}
        isLoading={isLoading}
      /> */}
    </div>
  );
};

export default NotasList;